import { Response } from './Common'
import { User } from './User'
import { FlattenType } from './typeHelper'

export type LoginPayload = {
  email: string
  password: string
}

export type SignupPayload = {
  fullName: string
  birthdate: string
  email: string
  clientKey?: string
  identifier?: string
  gender: string
  password: string
  addressId: number
  phone: string
  isoCode: string
  ambassadorCode: string
  mediaId: number | null
  hand: string
  defaultRating: string
  singlesRating: string
  doublesRating: string
  userId: number | null
  marketingOptIn: boolean
  termsOfService: boolean
  // The stage-2 finalize token from the email-first signup. Required by the server — no account is
  // created without it, and it binds the signup to the verified email.
  signupToken?: string
}

export type SignupRedemption = {
  finalizeToken: string
  email: string
  // Set when support has recorded verified parental consent on a pre-created (under-13) account. The
  // server then forces the stored name and birthdate on signup, so the form locks them and skips its
  // own under-13 block. Absent on older servers — treat as false.
  ageVerified?: boolean
  // The pre-created account's stored details. Only present when `ageVerified` is true.
  prefill?: {
    firstName: string | null
    lastName: string | null
    fullName: string
    birthdate: string // YYYY-MM-DD
    gender: 'MALE' | 'FEMALE' | null
  } | null
}

export type RegisterDevicePayload = {
  platform: string
  uuid: string
  token: string
  userId: number
}

export type LoginExternalApplicationPayload = {
  email: string
  password: string
  partnerId: string
}

export type ConsentPermissionCategory = 'REQUIRED' | 'OPTIONAL' | 'BLOCKED'

/**
 * One consent a partner asks for, exactly as the server describes it.
 *
 * Screens render these in the order they arrive and never look a key up in a table of their own:
 * keys are data (`pii.name`, `benefits.view`, whatever the server adds next), so a new or newly
 * required consent needs no web release. `required`, `description`, `labelKey` and `categoryUrn`
 * are optional only because an older server does not send them — see `isRequiredPermission`.
 */
export type ConsentPermissionDescriptor = {
  key: string
  label: string
  category: ConsentPermissionCategory
  /** One line on what sharing it means; absent or null when there is none. */
  description?: string | null
  /** A translation key a client may prefer over `label`, when it ships one. */
  labelKey?: string | null
  /** Connecting grants it and it cannot be switched off. Falls back to `category` when absent. */
  required?: boolean
  categoryUrn?: string
}

/**
 * The fixed-field consent payload older servers read. Still sent alongside `grants` so a web build
 * deployed ahead of the API keeps working; a current server ignores it whenever `grants` is present.
 */
export type UserPiiPermissions = {
  name: boolean
  partialName: boolean
  email: boolean
  age: boolean
  gender: boolean
  location: boolean
  ratings: boolean
  birthYear: boolean
  profilePhoto: boolean
  duprId: boolean
}

export type ConsentChallengeResponse = {
  consentToken: string
  partnerName: string
  permissions: ConsentPermissionDescriptor[]
  existingPermissions?: UserPiiPermissions
  /** Which of `permissions` the member already granted, by key. Absent on older servers. */
  existingGrants?: Record<string, boolean> | null
  consentAlreadyGranted: boolean
}

export type ConsentGrantRequest = {
  consentToken: string
  permissions: UserPiiPermissions
  useExistingConsent: boolean
  /** The key-based answer, one entry per descriptor shown. Wins over `permissions` when present. */
  grants?: Record<string, boolean>
}

export type ForgotPasswordPayload = {
  email: string
  code: string
  password: string
}

export type RequiredAction =
  | 'TWO_FACTOR_SETUP'
  /**
   * A login-time second factor must be completed. Distinct from TWO_FACTOR_SETUP, which is about
   * enrolling and is enforced once a session already exists — this one is returned INSTEAD of a
   * session, on a 428 from the login call itself.
   */
  | 'TWO_FACTOR_CHALLENGE'
  | 'PHONE_VERIFICATION'
  | 'EMAIL_VERIFICATION'
  | 'SUBSCRIPTION_REQUIRED'

// ==================== Strong-MFA rich model (URN-tagged) ====================
// The successor to the flat `requiredActions` / `twoFactorMethods` fields: the 428 body now also
// carries `requirements`, a list of URN-tagged actions whose payload is inline. Strong factors
// (TOTP/passkey) appear ONLY here (never in the legacy `twoFactorMethods`), so the client must read
// `requirements` to offer them. The legacy fields stay populated for un-migrated clients.

export const SECOND_FACTOR_URN = {
  EMAIL: 'urn:dupr:second-factor:email',
  SMS: 'urn:dupr:second-factor:sms',
  PHONE: 'urn:dupr:second-factor:phone',
  TOTP: 'urn:dupr:second-factor:totp',
  WEBAUTHN: 'urn:dupr:second-factor:webauthn',
  PUSH: 'urn:dupr:second-factor:push',
  RECOVERY: 'urn:dupr:second-factor:recovery',
} as const
export type SecondFactorUrn = (typeof SECOND_FACTOR_URN)[keyof typeof SECOND_FACTOR_URN]

/** A URN-identified second-factor method, e.g. `{ type: 'urn:dupr:second-factor:totp' }`. */
export type SecondFactorKind = { type: SecondFactorUrn }

/** The bare slug (`totp`, `webauthn`, …) of a kind — the last URN segment, for UI branching. */
export const secondFactorSlug = (kind: SecondFactorKind): string => kind.type.split(':').pop() ?? kind.type

export const REQUIRED_ACTION_URN = {
  TWO_FACTOR_CHALLENGE: 'urn:dupr:required-action:two-factor-challenge',
  STRONG_MFA_SETUP: 'urn:dupr:required-action:strong-mfa-setup',
  TWO_FACTOR_SETUP: 'urn:dupr:required-action:two-factor-setup',
  PHONE_VERIFICATION: 'urn:dupr:required-action:phone-verification',
  EMAIL_VERIFICATION: 'urn:dupr:required-action:email-verification',
  SUBSCRIPTION_REQUIRED: 'urn:dupr:required-action:subscription-required',
  PASSWORD_RESET_REQUIRED: 'urn:dupr:required-action:password-reset-required',
  FEATURE_QUOTA: 'urn:dupr:required-action:feature-quota',
  ACCOUNT_BANNED: 'urn:dupr:required-action:account-banned',
  ACCOUNT_RESTRICTED: 'urn:dupr:required-action:account-restricted',
  BOT_FLAGGED: 'urn:dupr:required-action:bot-flagged',
  LIMITED_VIEW: 'urn:dupr:required-action:limited-view',
} as const
export type RequiredActionUrn = (typeof REQUIRED_ACTION_URN)[keyof typeof REQUIRED_ACTION_URN]

export type TwoFactorChallengeRequirement = {
  type: typeof REQUIRED_ACTION_URN.TWO_FACTOR_CHALLENGE
  challengeToken: string
  methods: SecondFactorKind[]
  defaultMethod: SecondFactorKind
}
export type StrongMfaSetupRequirement = {
  type: typeof REQUIRED_ACTION_URN.STRONG_MFA_SETUP
  allowedMethods: SecondFactorKind[]
}
/**
 * The forced-reset halt at login completion. `resetToken` is present when the server halted right
 * after the second factor (password + code just proven): a short-lived single-use token the client
 * redeems with the new password to reset IN PLACE and finish the login. Absent on the request-path
 * / refresh 428s, where the client falls back to the emailed reset flow.
 */
export type PasswordResetRequiredRequirement = {
  type: typeof REQUIRED_ACTION_URN.PASSWORD_RESET_REQUIRED
  resetToken?: string
}
/** A required action from the 428 body. The variants above carry payload; the rest are bare. */
export type RichRequiredAction =
  | TwoFactorChallengeRequirement
  | StrongMfaSetupRequirement
  | PasswordResetRequiredRequirement
  | { type: RequiredActionUrn }

/** Pull the login two-factor challenge out of a `requirements` list, if present. */
export const findTwoFactorChallenge = (
  requirements?: RichRequiredAction[]
): TwoFactorChallengeRequirement | undefined =>
  requirements?.find((r): r is TwoFactorChallengeRequirement => r.type === REQUIRED_ACTION_URN.TWO_FACTOR_CHALLENGE)

export const hasStrongMfaSetup = (requirements?: RichRequiredAction[]): boolean =>
  !!requirements?.some((r) => r.type === REQUIRED_ACTION_URN.STRONG_MFA_SETUP)

/** Find a required action by its URN, if present in the list. */
export const findRequirement = (
  requirements: RichRequiredAction[] | undefined,
  urn: RequiredActionUrn
): RichRequiredAction | undefined => requirements?.find((r) => r.type === urn)

export const hasPasswordReset = (requirements?: RichRequiredAction[]): boolean =>
  !!findRequirement(requirements, REQUIRED_ACTION_URN.PASSWORD_RESET_REQUIRED)

/** The forced-reset requirement, if present — check `resetToken` for the in-place path. */
export const findPasswordResetRequirement = (
  requirements?: RichRequiredAction[]
): PasswordResetRequiredRequirement | undefined =>
  requirements?.find(
    (r): r is PasswordResetRequiredRequirement => r.type === REQUIRED_ACTION_URN.PASSWORD_RESET_REQUIRED
  )

export const hasSubscriptionRequired = (requirements?: RichRequiredAction[]): boolean =>
  !!findRequirement(requirements, REQUIRED_ACTION_URN.SUBSCRIPTION_REQUIRED)

export const hasLimitedView = (requirements?: RichRequiredAction[]): boolean =>
  !!findRequirement(requirements, REQUIRED_ACTION_URN.LIMITED_VIEW)

/**
 * The 428 "user action required" error body. It carries BOTH the legacy fields (kept for
 * un-migrated clients) and the rich, URN-tagged `requirements` — branch on the latter. The legacy
 * `errorCode` is lossy (it collapses several concurrent actions to "MULTIPLE_ACTIONS_REQUIRED"),
 * which is why the rich array is the source of truth.
 */
export type UserActionRequiredBody = {
  errorCode?: string
  requiredActions?: string[]
  requirements?: RichRequiredAction[]
  challengeToken?: string
  twoFactorMethods?: string[]
  defaultMethod?: string
  message?: string
}

/**
 * Pull the rich `requirements` array off whichever error shape a caller happens to hold. Two shapes
 * appear in this app: a raw axios rejection (`error.response.data.requirements`) and the flattened
 * value redux's `rejectWithValue` produces, which spreads the body onto the error itself
 * (`err.requirements` — see authSlice). Returns undefined when neither shape carries it.
 */
export const requirementsFrom = (error: unknown): RichRequiredAction[] | undefined => {
  const err = error as {
    requirements?: RichRequiredAction[]
    response?: { data?: { requirements?: RichRequiredAction[] } }
  }
  return err?.response?.data?.requirements ?? err?.requirements
}

// New strong-MFA endpoint payloads/results.
export type VerifyTwoFactorPayload = { challengeToken: string; code: string; method?: SecondFactorKind }
export type TotpSetupResult = { otpauthUri: string; secret: string; issuer: string }
export type TwoFactorStatus = {
  enabled: boolean
  enabledAt?: string
  canEnable: boolean
  totpEnrolled: boolean
  recoveryCodesRemaining: number
}
export type WebAuthnCredentialSummary = { credentialId: string; label?: string; created?: string; lastUsed?: string }

export type LoginResult = {
  accessToken: string
  refreshToken: string
  user: User
  requiredActions?: RequiredAction[]
  /**
   * Set instead of the tokens when the API answers a login with a second-factor challenge.
   *
   * `accessToken` and `refreshToken` are typed non-optional because that is what an ORDINARY login
   * returns and widening them would push a null check into every existing call site. Callers that
   * can render a code box must branch on `twoFactorRequired` BEFORE reading them -- see
   * ExternalLoginPage. Clients that do not send the mfa-challenge capability header never receive
   * this shape, which is what keeps the older ones working.
   */
  twoFactorRequired?: boolean
  challengeToken?: string
  /** The rich, URN-tagged requirements (source of truth for strong factors); read alongside the legacy fields. */
  requirements?: RichRequiredAction[]
  /** Legacy delivered-code methods (EMAIL/SMS/PHONE only) — strong factors live in `requirements`. */
  twoFactorMethods?: string[]
  defaultMethod?: string
  message?: string
}

export type RefreshResponse = FlattenType<
  Response & {
    result: string
  }
>

export type LoginResponse = FlattenType<
  Response & {
    message: string
    result: LoginResult
  }
>

export type SessionResponse = FlattenType<
  Response & {
    sessionId: string
    status: string
    paymentStatus: string
  }
>

export type BillingPortalLink = FlattenType<
  Response & {
    url: string
  }
>

export type EntitlementResponse = FlattenType<
  Response & {
    entitlement: Map<string, Array<string>>
  }
>

export type Subscription = {
  status: string
  displayName: string
  entitlements: Record<string, Array<string>>
}

export type SubscriptionResponse = FlattenType<
  Response & {
    subscriptions: Array<Subscription>
  }
>
export type LogoutResponse = Response
